import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AccountBalanceWallet as WalletIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateFormat';

const ChildOverview = ({
  child,
  childDetails,
  attendanceStats,
  todayAttendanceStatus,
  todayRoutine,
  feeTransactions,
  marks,
  subjects,
  loading,
  onSectionSelect,
  unreadFeedbackCount = 0,
}) => {
  const sections = [
    {
      key: 'todayClasses',
      label: "Today's Classes",
      icon: <ScheduleIcon />,
      subtitle: todayRoutine.status === 'schedule'
        ? `${todayRoutine.entries.length} classes`
        : todayRoutine.message || 'Check schedule',
      color: '#2196F3',
    },
    {
      key: 'feeTransactions',
      label: 'Fees',
      icon: <WalletIcon />,
      subtitle: feeTransactions.length ? `${feeTransactions.length} records` : 'No payments yet',
      color: '#4CAF50',
    },
    {
      key: 'attendance',
      label: 'Attendance',
      icon: <CalendarIcon />,
      subtitle: attendanceStats?.total_percentage !== undefined ? `${attendanceStats.total_percentage}% this term` : 'No records',
      color: '#FF9800',
    },
    {
      key: 'marks',
      label: 'Marks',
      icon: <AssignmentIcon />,
      subtitle: marks.length ? `${marks.length} exams` : 'Results pending',
      color: '#9C27B0',
    },
    {
      key: 'subjects',
      label: 'Subjects',
      icon: <SchoolIcon />,
      subtitle: subjects.length ? `${subjects.length} subjects` : 'No subjects yet',
      color: '#F44336',
    },
    {
      key: 'chat',
      label: 'Feedback',
      icon: <MessageIcon />,
      subtitle: unreadFeedbackCount > 0 ? `${unreadFeedbackCount} unread` : 'View feedback',
      color: '#00BCD4',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 2 }}>
      {/* Sections Grid */}
      <Box sx={{ px: 2 }}>
        <Grid container spacing={2}>
          {sections.map((section) => (
            <Grid item xs={6} key={section.key}>
              <Paper
                onClick={() => onSectionSelect(section.key)}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  minHeight: 160,
                  background: `linear-gradient(135deg, ${section.color}08 0%, ${section.color}03 100%)`,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '1px solid',
                  borderColor: `${section.color}20`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${section.color} 0%, ${section.color}CC 100%)`,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.02)',
                    boxShadow: `0 12px 40px ${section.color}30, 0 4px 12px rgba(0,0,0,0.1)`,
                    borderColor: `${section.color}60`,
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                    '& .icon-container': {
                      transform: 'scale(1.1) rotate(5deg)',
                      boxShadow: `0 8px 24px ${section.color}40`,
                    },
                  },
                }}
              >
                <Box
                  className="icon-container"
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}DD 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    mb: 1.5,
                    boxShadow: `0 4px 16px ${section.color}40`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 100%)',
                      pointerEvents: 'none',
                    },
                    '& svg': {
                      fontSize: 28,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                    },
                  }}
                >
                  {section.icon}
                </Box>
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                  <Typography 
                    variant="body1" 
                    fontWeight={800} 
                    color="text.primary" 
                    sx={{ 
                      mb: 0.75,
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      letterSpacing: '-0.01em',
                      textAlign: 'center',
                    }}
                  >
                    {section.label}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      lineHeight: 1.4,
                      display: 'block',
                      textAlign: 'center',
                    }}
                  >
                    {section.subtitle}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default ChildOverview;

